import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Mention from "@tiptap/extension-mention";
import Link from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extensions";
import { Editor, getMarkRange } from "@tiptap/core";
import suggestions from "../../../../../../../../../../../components/RichTextEditor/utils/functions/get-suggestions-options";
import { useOrgUsersList } from "../../../../../../../../../../../hooks/useOrgUsersList";
import LeadCommentIcon from "../../../../../../../../../../../assets/svg/lead-comment-icon";
import VerbamailIcon from "../../../../../../../../../../../assets/svg/verbamail-icon";
import CheckIcon from "../../../../../../../../../../../assets/svg/check-icon";
import PlusFilledIcon from "../../../../../../../../../../../assets/svg/plus-filled-icon";
import SimpleDocumentIcon from "../../../../../../../../../../../assets/svg/simple-document-icon";
import ChevronDownIcon from "../../../../../../../../../../../assets/svg/chevron-down-icon";
import AttachmentInputIcon from "../../../../../../../../../../../assets/svg/attachment-input-icon";
import EmojiIcon from "../../../../../../../../../../../assets/svg/emoji-icon";
import BoldIcon from "../../../../../../../../../../../assets/svg/bold-icon";
import ItalicsIcon from "../../../../../../../../../../../assets/svg/italics-icon";
import UnderlineIcon from "../../../../../../../../../../../assets/svg/underline-icon";
import StrikethroughIcon from "../../../../../../../../../../../assets/svg/strikethrough-icon";
import UnorderedListIcon from "../../../../../../../../../../../assets/svg/unordered-list-icon";
import NumberedListIcon from "../../../../../../../../../../../assets/svg/numbered-list-icon";
import LinkIcon from "../../../../../../../../../../../assets/svg/link-icon";
import SendIcon from "../../../../../../../../../../../assets/svg/send-icon";
import UploadIcon from "../../../../../../../../../../../assets/svg/upload-icon";
import { EmojiPicker } from "../../../../../../../../../../../components/ComposeEmailEditor/components/Toolbar";
import AttachmentInput, {
  AttachmentInputRef,
  AttachmentItem,
} from "../../../../../../../../../../../components/Common/AttachmentInput";
import { Tooltip } from "../../../../../../../../../../../components/Tooltip/Tooltip";
import { TruncatedTextTooltip } from "../../../../../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import CircularLoader from "../../../../../../../../../../../components/Loader/CircularLoader";
import { createHandleAttachmentsChange } from "../../../../../../../../../../Communications/utils/sendAttachmentFunctions";
import type { TicketCommentAttachment } from "./CommentCard";
import {
  useCreateTicketCommentMutation,
  useUpdateTicketCommentMutation,
  useUploadHelpdeskAttachmentMutation,
} from "../../../../../../../../../../../redux/services/helpdeskApi";
import Button from "../../../../../../../../../../../components/Button/Button";
import { customToast } from "../../../../../../../../../../../utils/custom-toast/custom-toast";
export type TicketChannelType = "comment" | "verbamail";

interface ChannelOption {
  value: TicketChannelType;
  label: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  iconClassName: string;
}

const CHANNEL_OPTIONS: ChannelOption[] = [
  {
    value: "comment",
    label: "Comment",
    description: "Write a comment",
    icon: LeadCommentIcon,
    iconClassName: "w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk",
  },
  {
    value: "verbamail",
    label: "VerbaMail",
    description: "E-Mail communications",
    icon: VerbamailIcon,
    iconClassName: "w-[18px] h-[18px] text-[#F5A623]",
  },
];

const useAnchoredPortal = (
  isOpen: boolean,
  triggerRef: RefObject<HTMLElement>,
  dropdownWidth: number = 200,
  dropdownHeightHint: number = 300
) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [positioned, setPositioned] = useState(false);
  const portalRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const buffer = 8;
    const actualHeight = portalRef.current?.offsetHeight || dropdownHeightHint;

    const spaceRight = viewportWidth - rect.left - buffer;
    const spaceLeft = rect.right - buffer;
    const shouldAlignRight =
      spaceRight < dropdownWidth && spaceLeft >= dropdownWidth;

    let top = rect.top - actualHeight - buffer;
    let left = shouldAlignRight ? rect.right - dropdownWidth : rect.left;

    left = Math.max(
      buffer,
      Math.min(left, viewportWidth - dropdownWidth - buffer)
    );
    top = Math.max(
      buffer,
      Math.min(top, viewportHeight - actualHeight - buffer)
    );

    setPosition({ top, left });
    setPositioned(true);
  }, [triggerRef, dropdownWidth, dropdownHeightHint]);

  useEffect(() => {
    if (!isOpen) {
      setPositioned(false);
      return;
    }
    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener("resize", handleUpdate);

    const blockScroll = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("scroll", blockScroll, {
      passive: false,
      capture: true,
    });
    window.addEventListener("wheel", blockScroll, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchmove", blockScroll, {
      passive: false,
      capture: true,
    });

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", blockScroll, true);
      window.removeEventListener("wheel", blockScroll, true);
      window.removeEventListener("touchmove", blockScroll, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const isInsideToolbarPortal = (node: EventTarget | null) =>
      node instanceof Element && !!node.closest("[data-toolbar-portal]");

    const guardFocusEvent = (event: FocusEvent) => {
      if (
        isInsideToolbarPortal(event.target) ||
        isInsideToolbarPortal(event.relatedTarget)
      ) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("focusin", guardFocusEvent, true);
    document.addEventListener("focusout", guardFocusEvent, true);
    return () => {
      document.removeEventListener("focusin", guardFocusEvent, true);
      document.removeEventListener("focusout", guardFocusEvent, true);
    };
  }, [isOpen]);

  const portalRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      (portalRef as MutableRefObject<HTMLDivElement | null>).current = node;
      if (node) requestAnimationFrame(updatePosition);
    },
    [updatePosition]
  );

  const renderPortal = (
    children: ReactNode,
    wrapperProps?: Record<string, unknown>
  ) => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={portalRefCallback}
        data-toolbar-portal
        {...wrapperProps}
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          zIndex: 9999999,
          pointerEvents: "auto",
          visibility: positioned ? "visible" : "hidden",
        }}
      >
        {children}
      </div>,
      document.body
    );
  };

  return { renderPortal };
};

interface TicketCommentInputProps {
  ticketId?: string;
  parentCommentId?: string;
  mode?: "create" | "edit";
  editCommentId?: string;
  initialContent?: string;
  initialAttachments?: TicketCommentAttachment[];
  onCancel?: () => void;
  onEditSuccess?: () => void;
  selectedChannel?: TicketChannelType;
  onChannelChange?: (channel: TicketChannelType) => void;
  onComposeEmail?: () => void;
  onTemplates?: () => void;
  isVerbaMailDisabled?: boolean;
}

export const TicketCommentInput = ({
  ticketId,
  parentCommentId,
  mode = "create",
  editCommentId,
  initialContent,
  initialAttachments,
  onCancel,
  onEditSuccess,
  selectedChannel = "comment",
  onChannelChange,
  onComposeEmail,
  onTemplates,
  isVerbaMailDisabled = false,
}: TicketCommentInputProps) => {
  const isEditMode = mode === "edit";
  const [isDragOver, setIsDragOver] = useState(false);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isListDropdownOpen, setIsListDropdownOpen] = useState(false);
  const [isChannelDropdownOpen, setIsChannelDropdownOpen] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [isLinkBubbleForcedHidden, setIsLinkBubbleForcedHidden] =
    useState(false);
  const [editMenuPosition, setEditMenuPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  } | null>(null);
  const [linkBubble, setLinkBubble] = useState<{
    href: string;
    top?: number;
    bottom?: number;
    startLeft: number;
    endRight: number;
  } | null>(null);
  const [linkBubbleWidth, setLinkBubbleWidth] = useState<number | null>(null);

  const measureLinkBubble = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setLinkBubbleWidth(node.getBoundingClientRect().width);
    }
  }, []);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [linkUrlError, setLinkUrlError] = useState("");
  const [attachments, setAttachments] = useState<AttachmentItem[]>(() => {
    if (isEditMode && initialAttachments?.length) {
      return initialAttachments?.map((att) => ({
        id: att.s3_url,
        file: null,
        name: att.file_name,
        mime: att.content_type,
        size: att.size_in_bytes,
        status: "sent" as const,
        remoteUrl: att.s3_key || att.s3_url,
        s3Url: att.s3_url,
        previewUrl: att.content_type?.startsWith("image/")
          ? att.s3_url
          : undefined,
      }));
    }
    return [];
  });
  const [editorUpdateKey, setForceUpdate] = useState(0);

  const listDropdownRef = useRef<HTMLDivElement>(null);
  const emojiDropdownRef = useRef<HTMLDivElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);
  const channelDropdownRef = useRef<HTMLDivElement>(null);
  const bubbleMenuRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const channelPortal = useAnchoredPortal(
    isChannelDropdownOpen,
    channelDropdownRef,
    220,
    230
  );
  const emojiPortal = useAnchoredPortal(
    isEmojiOpen,
    emojiDropdownRef,
    400,
    400
  );
  const linkPortal = useAnchoredPortal(
    isLinkPopoverOpen,
    linkPopoverRef,
    288,
    180
  );
  const listPortal = useAnchoredPortal(
    isListDropdownOpen,
    listDropdownRef,
    150,
    100
  );
  const attachmentInputRef = useRef<AttachmentInputRef>(null);
  const attachmentsRef = useRef(attachments);
  const isUpdatingRef = useRef(false);
  const handleSendRef = useRef<() => void>(() => {});

  const [uploadHelpdeskAttachment] = useUploadHelpdeskAttachmentMutation();
  const [createTicketComment, { isLoading: isSending }] =
    useCreateTicketCommentMutation();
  const [updateTicketComment, { isLoading: isUpdating }] =
    useUpdateTicketCommentMutation();
  const { activeUsers } = useOrgUsersList();
  const taggedUserIdsRef = useRef<string[]>([]);

  const mentionUsers = useMemo(
    () =>
      activeUsers.map((user) => ({
        _id: user._id,
        name: user.name,
        email: user?.email ?? "",
        profile_picture: null,
      })),
    [activeUsers]
  );

  const mentionExtensions = useMemo(
    () => [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
        link: false,
      }),
      Underline,
      Link.extend({ inclusive: false }).configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class:
            "text-verba-blue dark:text-verba-blue-dk underline cursor-pointer",
        },
      }),
      Placeholder.configure({
        placeholder: isEditMode ? "Edit your comment..." : "Type message...",
      }),
      Mention.configure({
        HTMLAttributes: {
          class:
            "text-sm inline-flex bg-tagged-user-bg dark:bg-tagged-user-bg-dk border border-card-yellow-stroke dark:border-card-yellow-stroke-dk rounded-[4px] text-tagged-user dark:text-tagged-user-dk font-medium px-[6px] py-[2px] mr-1 after:content-['\u200B'] whitespace-nowrap",
        },
        suggestion: suggestions(mentionUsers),
      }),
    ],
    [isEditMode, mentionUsers]
  );

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions: mentionExtensions,
      content: isEditMode && initialContent ? initialContent : "",
      onUpdate: ({ editor: ed }) => {
        setForceUpdate((prev) => prev + 1);
        const ids: string[] = [];
        ed.state.doc.descendants((node) => {
          if (
            node.type.name === "mention" &&
            node.attrs.id &&
            !ids?.includes(node.attrs.id)
          ) {
            ids?.push(node.attrs.id);
          }
        });
        taggedUserIdsRef.current = ids;
      },
      onSelectionUpdate: () => {
        setForceUpdate((prev) => prev + 1);
      },
      editorProps: {
        attributes: {
          class:
            "ticket-comment-editor outline-none min-h-[20px] text-md text-heading-text dark:text-heading-text-dk",
        },
        handleKeyDown: (_view, event) => {
          if (event.key === "Backspace") {
            setIsLinkBubbleForcedHidden(true);
          }
          if (event.key === "Enter" && !event.shiftKey && !isEditMode) {
            event?.preventDefault();
            handleSendRef.current();
            return true;
          }
          return false;
        },
        handlePaste: (_view, event) => {
          const files = event.clipboardData?.files;
          if (!files || files.length === 0) return false;
          const validFiles = Array.from(files).filter(
            (f) =>
              f.type.startsWith("image/") ||
              f.type === "application/pdf" ||
              f.type === "application/msword" ||
              f.type ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
              f.type === "text/csv"
          );
          if (validFiles.length === 0) return false;
          event.preventDefault();
          const dt = new DataTransfer();
          validFiles.forEach((f) => dt.items.add(f));
          attachmentInputRef.current?.addFiles(dt.files);
          return true;
        },
      },
    },
    [mentionExtensions]
  );

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  const handleAttachmentsChange = useMemo(
    () =>
      createHandleAttachmentsChange(
        isUpdatingRef,
        attachmentsRef,
        setAttachments,
        uploadHelpdeskAttachment
      ),
    [uploadHelpdeskAttachment]
  );

  const canSend = useMemo(() => {
    if (!editor) return false;
    const text = editor.getText()?.trim();
    return text?.length > 0 || attachments?.length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, attachments?.length, editorUpdateKey]);

  const handleSend = useCallback(async () => {
    if (!editor) return;
    if (attachments?.some((att) => att.status === "uploading")) {
      customToast(
        "Attachments are still being uploaded. Please wait until all uploads complete.",
        "warning"
      );
      return;
    }
    const text = editor.getText()?.trim();
    if (!text && attachments?.length === 0) return;

    const uploadedAttachments = attachments
      .filter((att) => att.status === "sent" && att.remoteUrl)
      .map((att) => ({
        s3_key: att.remoteUrl!,
        file_name: att.name,
        content_type: att.mime || "",
        size_in_bytes: att?.size || 0,
        s3_url: att.s3Url || "",
      }));

    try {
      if (isEditMode && editCommentId) {
        await updateTicketComment({
          comment_id: editCommentId,
          content: editor.getHTML(),
          tagged_user_ids: taggedUserIdsRef.current,
          attachments: uploadedAttachments,
        })?.unwrap();
        onEditSuccess?.();
      } else {
        if (!ticketId) return;
        await createTicketComment({
          ticket_id: ticketId,
          content: editor.getHTML(),
          tagged_user_ids: taggedUserIdsRef.current,
          ...(parentCommentId && { parent_comment_id: parentCommentId }),
          ...(uploadedAttachments?.length > 0 && {
            attachments: uploadedAttachments,
          }),
        })?.unwrap();
        editor.commands.clearContent();
        setAttachments([]);
        taggedUserIdsRef.current = [];
      }
    } catch {
      // mutation error is handled by RTK Query
    }
  }, [
    editor,
    ticketId,
    parentCommentId,
    attachments,
    createTicketComment,
    isEditMode,
    editCommentId,
    updateTicketComment,
    onEditSuccess,
  ]);

  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  const handleFormatAction = useCallback(
    (action: (e: Editor) => void) => {
      if (!editor) return;
      action(editor);
      setForceUpdate((prev) => prev + 1);
    },
    [editor]
  );

  const isFormatActive = useCallback(
    (name: string) => {
      if (!editor) return false;
      return editor.isActive(name);
    },
    [editor]
  );

  const getActiveClass = (isActive: boolean) =>
    isActive ? "bg-card-highlight dark:bg-card-highlight-dk" : "";

  const handleListChange = useCallback(
    (listType: "bullet" | "number") => {
      if (!editor) return;
      if (listType === "bullet") {
        if (editor.isActive("bulletList")) {
          setIsListDropdownOpen(false);
          return;
        }
        if (editor.isActive("orderedList")) {
          editor.chain().focus().toggleOrderedList().toggleBulletList().run();
        } else {
          editor.chain().focus().toggleBulletList().run();
        }
      } else {
        if (editor.isActive("orderedList")) {
          setIsListDropdownOpen(false);
          return;
        }
        if (editor.isActive("bulletList")) {
          editor.chain().focus().toggleBulletList().toggleOrderedList().run();
        } else {
          editor.chain().focus().toggleOrderedList().run();
        }
      }
      setIsListDropdownOpen(false);
      setForceUpdate((prev) => prev + 1);
    },
    [editor]
  );

  const validateUrl = useCallback(
    (url: string): { isValid: boolean; error?: string } => {
      const trimmedUrl = url.trim();

      if (!trimmedUrl) {
        return { isValid: false, error: "URL is required" };
      }

      try {
        const urlObj = new URL(trimmedUrl);
        const validProtocols = ["http:", "https:", "mailto:", "tel:", "ftp:"];
        if (!validProtocols.includes(urlObj.protocol)) {
          return {
            isValid: false,
            error:
              "Invalid protocol. Use http://, https://, mailto:, tel:, or ftp://",
          };
        }

        if (
          (urlObj.protocol === "http:" || urlObj.protocol === "https:") &&
          !urlObj.hostname
        ) {
          return { isValid: false, error: "Invalid URL format" };
        }

        return { isValid: true };
      } catch {
        let fixedUrl = trimmedUrl;

        if (!/^[a-zA-Z]+:\/\//.test(fixedUrl)) {
          fixedUrl = "https://" + fixedUrl;
        }

        try {
          new URL(fixedUrl);
          return { isValid: true };
        } catch {
          return { isValid: false, error: "Please enter a valid URL" };
        }
      }
    },
    []
  );

  const handleLinkButtonClick = useCallback(() => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    setLinkText(selectedText);
    setLinkUrl("");
    setLinkUrlError("");
    setIsLinkPopoverOpen((prev) => !prev);
  }, [editor]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) return;
    const { to } = editor.state.selection;
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .unsetLink()
      .setTextSelection(to)
      .run();
    setIsEditingLink(false);
    setIsLinkBubbleForcedHidden(false);
  }, [editor]);

  const handleEditLink = useCallback(() => {
    if (!editor) return;

    editor.chain().focus().extendMarkRange("link").run();

    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, " ");
    const linkAttrs = editor.getAttributes("link");

    setLinkText(text);
    setLinkUrl(linkAttrs.href ?? "");
    setLinkUrlError("");
    setIsEditingLink(true);
  }, [editor]);

  const handleApplyLink = useCallback(() => {
    if (!editor) return;
    const url = linkUrl.trim();
    const text = linkText.trim();
    const { from, to } = editor.state.selection;
    const range = { from, to };

    if (!url || !text) {
      if (!url) {
        setLinkUrlError("URL is required");
      }
      return;
    }

    const validation = validateUrl(url);
    if (!validation.isValid) {
      setLinkUrlError(validation.error || "Invalid URL");
      return;
    }

    setLinkUrlError("");

    let normalizedUrl = url;
    if (!/^[a-zA-Z]+:\/\//.test(normalizedUrl)) {
      normalizedUrl = "https://" + normalizedUrl;
    }

    editor
      .chain()
      .focus()
      .insertContentAt(range, {
        type: "text",
        text: text,
        marks: [
          {
            type: "link",
            attrs: { href: normalizedUrl },
          },
        ],
      })
      .setTextSelection(range.from + text.length)
      .unsetMark("link")
      .run();

    setIsLinkPopoverOpen(false);
    setIsEditingLink(false);
    setIsLinkBubbleForcedHidden(false);
  }, [editor, linkUrl, linkText, validateUrl]);

  const handleLinkUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLinkUrl(e.target.value);
      if (linkUrlError) {
        setLinkUrlError("");
      }
    },
    [linkUrlError]
  );

  const handleAddAttachment = () => {
    attachmentInputRef.current?.triggerFilePicker();
    setTimeout(() => {
      if (editor) {
        editor.chain().focus().run();
      }
    }, 150);
  };

  const handleEmojiToggle = () => {
    setIsEmojiOpen((prev) => !prev);
  };

  const dropContainerRef = useRef<HTMLDivElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropContainerRef.current?.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    attachmentInputRef.current?.addFiles(files);
  }, []);

  const dragProps = {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target?.closest?.("[data-list-dropdown]")) {
        setIsListDropdownOpen(false);
      }
      if (!target?.closest?.("[data-channel-dropdown]")) {
        setIsChannelDropdownOpen(false);
      }
      if (!target?.closest?.("[data-link-popup]")) {
        setIsLinkPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleEmojiClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        target?.closest("[data-emoji-popup-button]") ||
        target?.closest("[data-emoji-popup-container]")
      )
        return;
      setIsEmojiOpen(false);
    };
    window.addEventListener("click", handleEmojiClickOutside);
    return () => window.removeEventListener("click", handleEmojiClickOutside);
  }, []);

  useEffect(() => {
    if (!isEditingLink) return;

    const handleClickOutsideEdit = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.("[data-link-bubble]")) {
        return;
      }
      if (
        bubbleMenuRef.current &&
        !bubbleMenuRef.current.contains(event.target as Node)
      ) {
        setIsEditingLink(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideEdit);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideEdit);
  }, [isEditingLink]);

  useEffect(() => {
    if (!isEditingLink) return;

    const isInsideToolbarPortal = (node: EventTarget | null) =>
      node instanceof Element && !!node.closest("[data-toolbar-portal]");

    const guardFocusEvent = (event: FocusEvent) => {
      if (
        isInsideToolbarPortal(event.target) ||
        isInsideToolbarPortal(event.relatedTarget)
      ) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("focusin", guardFocusEvent, true);
    document.addEventListener("focusout", guardFocusEvent, true);
    return () => {
      document.removeEventListener("focusin", guardFocusEvent, true);
      document.removeEventListener("focusout", guardFocusEvent, true);
    };
  }, [isEditingLink]);

  useEffect(() => {
    if (isEditingLink && editor && editorContainerRef.current) {
      const { from } = editor.state.selection;
      const coords = editor.view.coordsAtPos(from);

      const MIN_SPACE_ABOVE = 190;
      const POPUP_WIDTH = 320;
      const EDGE_BUFFER = 8;

      const clampedLeft = Math.max(
        EDGE_BUFFER,
        Math.min(coords.left, window.innerWidth - POPUP_WIDTH - EDGE_BUFFER)
      );

      if (coords.top >= MIN_SPACE_ABOVE) {
        setEditMenuPosition({
          bottom: window.innerHeight - coords.top + 8,
          left: clampedLeft,
        });
      } else {
        setEditMenuPosition({
          top: coords.bottom + 8,
          left: clampedLeft,
        });
      }
    }
  }, [isEditingLink, editor, editor?.state.selection]);

  useEffect(() => {
    if (!editor) return;
    const bumpUpdate = () => setForceUpdate((prev) => prev + 1);
    const resetForcedHiddenOnBlur = () => setIsLinkBubbleForcedHidden(false);
    editor.on("focus", bumpUpdate);
    editor.on("blur", bumpUpdate);
    editor.on("blur", resetForcedHiddenOnBlur);
    return () => {
      editor.off("focus", bumpUpdate);
      editor.off("blur", bumpUpdate);
      editor.off("blur", resetForcedHiddenOnBlur);
    };
  }, [editor]);

  useEffect(() => {
    if (
      !editor ||
      isEditingLink ||
      isLinkBubbleForcedHidden ||
      !editor.isFocused
    ) {
      setLinkBubble(null);
      return;
    }

    const { selection } = editor.state;
    if (!selection.empty || !editor.isActive("link")) {
      setLinkBubble(null);
      return;
    }

    const $pos = selection.$from;
    const linkMark = $pos.marks().find((mark) => mark.type.name === "link");
    const href = linkMark?.attrs.href as string | undefined;

    if (!href || href.trim() === "") {
      setLinkBubble(null);
      return;
    }

    if ($pos.parentOffset === $pos.parent.nodeSize - 2) {
      setLinkBubble(null);
      return;
    }

    const linkRange = getMarkRange($pos, editor.schema.marks.link);
    const cursorCoords = editor.view.coordsAtPos(selection.from);
    const startCoords = linkRange
      ? editor.view.coordsAtPos(linkRange.from)
      : cursorCoords;
    const endCoords = linkRange
      ? editor.view.coordsAtPos(linkRange.to)
      : cursorCoords;

    const MIN_SPACE_ABOVE = 60;

    const vertical =
      cursorCoords.top >= MIN_SPACE_ABOVE
        ? { bottom: window.innerHeight - cursorCoords.top + 8 }
        : { top: cursorCoords.bottom + 8 };

    setLinkBubble({
      href,
      ...vertical,
      startLeft: startCoords.left,
      endRight: endCoords.right,
    });
  }, [editor, editorUpdateKey, isEditingLink, isLinkBubbleForcedHidden]);

  const isUploading = attachments?.some((att) => att.status === "uploading");
  const isProcessing = isSending || isUpdating;

  const handleChannelSelect = (channel: TicketChannelType) => {
    if (channel === "verbamail" && isVerbaMailDisabled) return;
    onChannelChange?.(channel);
    setIsChannelDropdownOpen(false);
  };

  const handleHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!editor) return;
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("input") ||
      target.closest("[contenteditable]") ||
      target.closest("[data-link-popup], [data-link-bubble]")
    ) {
      return;
    }
    const endOfFirstLine = 1 + (editor.state.doc.firstChild?.content.size ?? 0);
    editor.chain().focus().setTextSelection(endOfFirstLine).run();
  };

  const activeChannel =
    CHANNEL_OPTIONS.find((opt) => opt.value === selectedChannel) ??
    CHANNEL_OPTIONS[0];

  const renderChannelSelector = () => {
    const Icon = activeChannel.icon;

    return (
      <div className="relative shrink-0" data-channel-dropdown ref={channelDropdownRef}>
        <button
          className="flex items-center gap-0.5 p-1 cursor-pointer rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
          onClick={() => setIsChannelDropdownOpen((prev) => !prev)}
        >
          <Icon className={activeChannel.iconClassName} />
          <span className="pr-1 shrink-0 text-md font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap">
            {activeChannel?.label}
          </span>
          <ChevronDownIcon
            className={`w-[9px] h-[9px] text-verba-indigo dark:text-verba-indigo-dk transition-transform ${
              isChannelDropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {channelPortal.renderPortal(
          <div className="p-1.5 flex flex-col gap-0.5 border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk rounded-lg shadow-popper-blur-effect backdrop-blur-2xl min-w-[220px]">
            {CHANNEL_OPTIONS.map((option) => {
              const OptionIcon = option.icon;
              const isSelected = option.value === selectedChannel;
              const isDisabled =
                option.value === "verbamail" && isVerbaMailDisabled;

              const optionButton = (
                <button
                  key={option.value}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md w-full ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : isSelected
                        ? "bg-hover-effect dark:bg-hover-effect-dk cursor-pointer"
                        : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer"
                  }`}
                  onClick={() => handleChannelSelect(option.value)}
                  disabled={isDisabled}
                >
                  <OptionIcon className={option.iconClassName} />
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <span className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
                      {option.label}
                    </span>
                    <span className="text-xs font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
                      {option.description}
                    </span>
                  </div>
                  {isSelected && (
                    <CheckIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk shrink-0" />
                  )}
                </button>
              );

              if (isDisabled) {
                return (
                  <Tooltip
                    key={option.value}
                    content="Outlook integration not available for this account"
                    side="right"
                  >
                    {optionButton}
                  </Tooltip>
                );
              }

              return optionButton;
            })}
          </div>,
          { "data-channel-dropdown": "" }
        )}
      </div>
    );
  };

  const getLinkBubbleHorizontal = (bubble: NonNullable<typeof linkBubble>) => {
    const EDGE_BUFFER = 8;
    if (linkBubbleWidth === null) {
      return { left: Math.max(EDGE_BUFFER, bubble.startLeft) };
    }
    const fitsRight =
      bubble.startLeft + linkBubbleWidth + EDGE_BUFFER <= window.innerWidth;
    return fitsRight
      ? { left: Math.max(EDGE_BUFFER, bubble.startLeft) }
      : { right: Math.max(EDGE_BUFFER, window.innerWidth - bubble.endRight) };
  };

  const renderLinkBubble = () => {
    if (!editor) return null;
    return (
      <>
        {linkBubble &&
          !isEditingLink &&
          createPortal(
            <div
              key={linkBubble.href}
              ref={measureLinkBubble}
              data-link-bubble
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: "fixed",
                ...(linkBubble.top !== undefined
                  ? { top: linkBubble.top }
                  : {}),
                ...(linkBubble.bottom !== undefined
                  ? { bottom: linkBubble.bottom }
                  : {}),
                ...getLinkBubbleHorizontal(linkBubble),
                zIndex: 9999999,
              }}
              className="min-w-max max-w-[500px] flex items-center space-x-1 text-xs font-normal p-2 bg-pure-white dark:bg-verba-indigo border border-border-gray dark:border-gray-375 rounded-[4px] shadow-lg"
            >
              <span className="text-verba-indigo dark:text-verba-indigo-dk shrink-0">
                Go to link:
              </span>
              <span
                className="cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsLinkBubbleForcedHidden(true);
                  window.open(
                    linkBubble.href,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              >
                <TruncatedTextTooltip
                  text={linkBubble.href}
                  maxWidth="max-w-[180px]"
                  className="text-verba-blue dark:text-verba-blue-dk hover:underline"
                />
              </span>
              <span className="text-verba-indigo dark:text-verba-indigo-dk shrink-0">
                |
              </span>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleEditLink();
                }}
                className="text-verba-blue dark:text-verba-blue-dk hover:underline shrink-0"
              >
                Change
              </button>
              <span className="text-verba-indigo dark:text-verba-indigo-dk shrink-0">
                |
              </span>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleRemoveLink();
                }}
                className="text-verba-blue dark:text-verba-blue-dk hover:underline shrink-0"
              >
                Remove
              </button>
            </div>,
            document.body
          )}

        {isEditingLink &&
          editMenuPosition &&
          createPortal(
            <div
              ref={bubbleMenuRef}
              data-toolbar-portal
              data-link-popup
              style={{
                position: "fixed",
                ...(editMenuPosition.top !== undefined
                  ? { top: editMenuPosition.top }
                  : {}),
                ...(editMenuPosition.bottom !== undefined
                  ? { bottom: editMenuPosition.bottom }
                  : {}),
                left: editMenuPosition.left,
                zIndex: 9999999,
                pointerEvents: "auto",
              }}
              className="bg-pure-white dark:bg-verba-indigo border border-border-gray dark:border-gray-375 rounded-[4px] shadow-lg p-4 min-w-[320px]"
            >
              <div className="space-y-2">
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-2 py-1 border border-border-gray dark:border-gray-375 rounded focus:outline-none text-sm text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-verba-indigo"
                  placeholder="Text"
                />
                <div className="space-y-1">
                  <input
                    type="text"
                    value={linkUrl}
                    onChange={handleLinkUrlChange}
                    className={`w-full px-2 py-1 border rounded focus:outline-none text-sm text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-verba-indigo ${
                      linkUrlError
                        ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                        : "border-border-gray dark:border-gray-375"
                    }`}
                    placeholder="Type or paste a link"
                    autoFocus
                  />
                  {linkUrlError && (
                    <div className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                      {linkUrlError}
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setIsEditingLink(false);
                      setLinkUrlError("");
                    }}
                    className="px-4 py-1 text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk bg-gray-75 dark:bg-gray-375 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApplyLink}
                    disabled={!linkUrl || !linkText || !!linkUrlError}
                    className="disabled:opacity-50 px-4 py-1 text-sm font-medium text-fixed-white dark:text-fixed-white-dk bg-verba-blue dark:bg-verba-blue-dk rounded"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </>
    );
  };

  const renderCommentEditor = () => (
    <>
      <div
        ref={editorContainerRef}
        className="flex-1 pl-2 py-1 min-h-0 overflow-y-auto"
      >
        {renderLinkBubble()}
        <EditorContent editor={editor} />
      </div>

      <div
        className={`flex flex-col gap-[6px] shrink-0 ${
          isUploading ? "pointer-events-none opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <AttachmentInput
          ref={attachmentInputRef}
          value={attachments}
          onChange={handleAttachmentsChange}
          multiple
          accept="image/*,.pdf,.doc,.docx,.csv"
          maxFiles={5}
          onFilePickerClose={() => {
            if (editor) {
              setTimeout(() => {
                editor.chain().focus().run();
              }, 50);
            }
          }}
        />
      </div>
    </>
  );

  const renderToolbar = () => {
    if (!editor) return null;
    return (
      <div
        className="flex items-center justify-between pr-1.5 shrink-0"
        onMouseDown={(e) => {
          const target = e.target as HTMLElement;
          if (
            target?.closest("[data-no-prevent]") ||
            target?.closest("[data-emoji-popup-container]")
          )
            return;
          e?.preventDefault();
        }}
      >
        <div className="flex items-center gap-1.5">
          <Tooltip content="Attach a file" side="top">
            <button
              data-no-prevent
              className="p-0.5 shrink-0 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded mt-0.5"
              onClick={handleAddAttachment}
            >
              <AttachmentInputIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
            </button>
          </Tooltip>

          <div className="relative shrink-0" ref={emojiDropdownRef}>
            <Tooltip content="Add Emoji" side="top">
              <button
                data-emoji-popup-button
                className={`p-0.5 rounded cursor-pointer shrink-0 mt-[7px] ${
                  isEmojiOpen
                    ? "bg-hover-effect dark:bg-hover-effect-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
                onClick={handleEmojiToggle}
              >
                <EmojiIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
              </button>
            </Tooltip>

            {emojiPortal.renderPortal(
              <EmojiPicker
                editor={editor}
                onClose={() => setIsEmojiOpen(false)}
              />,
              { "data-emoji-popup-container": "" }
            )}
          </div>

          <div className="w-px h-[18px] bg-border-gray dark:bg-border-gray-dk shrink-0" />

          <button
            className={`p-0.5 flex items-center justify-center hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer shrink-0 ${getActiveClass(isFormatActive("bold"))}`}
            onClick={() =>
              handleFormatAction((e) => e.chain().focus().toggleBold().run())
            }
          >
            <BoldIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
          <button
            className={`p-0.5 flex items-center justify-center hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer shrink-0 ${getActiveClass(isFormatActive("italic"))}`}
            onClick={() =>
              handleFormatAction((e) => e.chain().focus().toggleItalic().run())
            }
          >
            <ItalicsIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
          <button
            className={`p-0.5 flex items-center justify-center hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer shrink-0 ${getActiveClass(isFormatActive("underline"))}`}
            onClick={() =>
              handleFormatAction((e) =>
                e.chain().focus().toggleUnderline().run()
              )
            }
          >
            <UnderlineIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
          <button
            className={`p-0.5 flex items-center justify-center hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer shrink-0 ${getActiveClass(isFormatActive("strike"))}`}
            onClick={() =>
              handleFormatAction((e) => e.chain().focus().toggleStrike().run())
            }
          >
            <StrikethroughIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>

          <div className="w-px h-[18px] bg-border-gray dark:bg-border-gray-dk shrink-0" />

          <div
            className="relative shrink-0"
            data-list-dropdown
            ref={listDropdownRef}
          >
            <button
              className={`flex items-center cursor-pointer shrink-0 ${getActiveClass(isFormatActive("bulletList") || isFormatActive("orderedList"))}`}
              onClick={() => setIsListDropdownOpen(!isListDropdownOpen)}
            >
              <div className="p-0.5 flex items-center justify-center rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk">
                <UnorderedListIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
              </div>
              <div className="size-[15px] flex items-center justify-center rounded-[2.5px]">
                <ChevronDownIcon
                  className={`w-2 h-2 text-dark-gray-fixed dark:text-dark-gray-fixed-dk transition-transform ${
                    isListDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {listPortal.renderPortal(
              <div className="p-1.5 flex flex-col gap-0.5 border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk rounded-lg shadow-popper-blur-effect backdrop-blur-2xl min-w-[150px]">
                <button
                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk w-full ${getActiveClass(isFormatActive("bulletList"))}`}
                  onClick={() => handleListChange("bullet")}
                >
                  <UnorderedListIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                  <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                    Bullet List
                  </span>
                </button>
                <button
                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk w-full ${getActiveClass(isFormatActive("orderedList"))}`}
                  onClick={() => handleListChange("number")}
                >
                  <NumberedListIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                  <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                    Numbered List
                  </span>
                </button>
              </div>,
              { "data-list-dropdown": "" }
            )}
          </div>

          <div
            className="relative shrink-0"
            data-link-popup
            ref={linkPopoverRef}
          >
            <Tooltip content="Link" side="top">
              <button
                data-no-prevent
                className={`p-0.5 flex items-center justify-center rounded cursor-pointer shrink-0 ${
                  isLinkPopoverOpen
                    ? "bg-card-highlight dark:bg-card-highlight-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
                onClick={handleLinkButtonClick}
              >
                <LinkIcon className="w-[18px] h-[18px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
              </button>
            </Tooltip>

            {linkPortal.renderPortal(
              <div
                className="w-72 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-popper-blur-effect backdrop-blur-2xl p-3 space-y-2.5"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-2 py-1 border border-border-gray dark:border-border-gray-dk rounded focus:outline-none text-md font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk"
                  placeholder="Text"
                />
                <div className="space-y-1">
                  <input
                    type="text"
                    value={linkUrl}
                    onChange={handleLinkUrlChange}
                    className={`w-full px-2 py-1 border rounded focus:outline-none text-md font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk ${
                      linkUrlError
                        ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                        : "border-border-gray dark:border-border-gray-dk"
                    }`}
                    placeholder="Type or paste a link"
                    autoFocus
                  />
                  {linkUrlError && (
                    <div className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                      {linkUrlError}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleApplyLink}
                    disabled={!linkUrl || !linkText || !!linkUrlError}
                    className="disabled:opacity-50 px-4 py-1 text-md font-medium text-pure-white dark:text-pure-white-dk bg-verba-blue dark:bg-verba-blue-dk rounded cursor-pointer"
                  >
                    Apply
                  </button>
                </div>
              </div>,
              { "data-link-popup": "", "data-no-prevent": "" }
            )}
          </div>
        </div>

        {!isEditMode && (
          <button
            className={`size-6 rounded-[6px] flex items-center justify-center cursor-pointer shrink-0 transition-colors ${
              canSend &&
              !isProcessing &&
              !isUploading &&
              "bg-verba-blue dark:bg-verba-blue-dk hover:opacity-90"
            }`}
            onClick={handleSend}
            disabled={!canSend || isProcessing || isUploading}
          >
            {isProcessing ? (
              <CircularLoader className="w-4 h-4 text-pure-white" />
            ) : (
              <SendIcon
                className={`w-4 h-4 shrink-0 mr-[3px] ${
                  canSend && !isUploading
                    ? "text-pure-white dark:text-pure-white-dk"
                    : "text-offline dark:text-offline-dk"
                } rotate-45`}
              />
            )}
          </button>
        )}
      </div>
    );
  };

  if (isEditMode) {
    const isDisabled = !canSend || isProcessing || isUploading;
    return (
      <div className="flex flex-col gap-[12px] py-[12px]">
        <div
          ref={dropContainerRef}
          className={`relative transition-[border-color,background-color] duration-200 ease-out rounded-lg ${
            isDragOver
              ? "border border-dashed border-blue-400/55 dark:border-blue-400/45 bg-blue-50/40 dark:bg-blue-950/20"
              : ""
          }`}
          {...dragProps}
        >
          {isDragOver && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-pure-white/60 dark:bg-pure-white-dk/60">
              <div className="flex flex-col items-center gap-1.5">
                <UploadIcon className="h-6 w-6 text-blue-400 dark:text-blue-500" />
                <p className="text-xs font-medium text-blue-500 dark:text-blue-400">
                  Drop files here
                </p>
              </div>
            </div>
          )}
          <div className={isDragOver ? "pointer-events-none opacity-30" : ""}>
            <div
              ref={editorContainerRef}
              className="overflow-y-auto px-[12px] min-h-[80px] max-h-[200px] [&_.tiptap]:min-h-[80px] [&_.tiptap]:w-full [&_.tiptap]:h-full [&_.tiptap]:outline-none"
            >
              {renderLinkBubble()}
              <EditorContent editor={editor} className="h-full w-full" />
            </div>
            <div
              className={`flex flex-col gap-[6px] px-[12px] shrink-0 ${
                isUploading
                  ? "pointer-events-none opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <AttachmentInput
                ref={attachmentInputRef}
                value={attachments}
                onChange={handleAttachmentsChange}
                multiple
                accept="image/*,.pdf,.doc,.docx,.csv"
                maxFiles={5}
                onFilePickerClose={() => {
                  if (editor) {
                    setTimeout(() => {
                      editor.chain().focus().run();
                    }, 50);
                  }
                }}
              />
            </div>
            {editor && <div className="px-[6px]">{renderToolbar()}</div>}
          </div>
        </div>
        <hr className="mx-[12px] border-border-gray dark:border-border-gray-dk" />
        <div className="flex justify-end gap-[8px] px-[12px]">
          <Button
            innerText="Cancel"
            onClick={onCancel}
            buttonContainerClass="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk bg-transparent dark:bg-transparent border border-verba-indigo dark:border-verba-indigo-dk rounded-[8px] py-[4px] px-[12px] w-[80px]"
          />
          <Button
            onClick={handleSend}
            isLoading={isProcessing}
            disabled={isDisabled}
            innerText="Update"
            buttonContainerClass={`text-md font-semibold text-pure-white dark:text-pure-white-dk bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-[8px] py-[4px] px-[12px] w-[80px] ${
              isDisabled ? "opacity-50 cursor-not-allowed!" : ""
            }`}
          />
        </div>
      </div>
    );
  }

  const isCommentMode = selectedChannel === "comment";

  const renderVerbaMailActions = () => (
    <div className="flex items-center gap-2 px-1 pb-1">
      <button
        className="flex items-center gap-1 cursor-pointer hover:opacity-80"
        onClick={onComposeEmail}
      >
        <PlusFilledIcon className="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk" />
        <span className="text-md font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap">
          Compose an Email
        </span>
      </button>

      <div className="w-px h-[18px] bg-border-gray dark:bg-border-gray-dk shrink-0" />

      <button
        className="flex items-center gap-1 cursor-pointer hover:opacity-80"
        onClick={onTemplates}
      >
        <SimpleDocumentIcon className="w-[18px] h-[18px]" />
        <span className="text-md font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap">
          Templates
        </span>
      </button>
    </div>
  );

  return (
    <div className="border-t border-border-gray dark:border-border-gray-dk p-3 pb-1.5 shrink-0">
      <div
        ref={dropContainerRef}
        className={`relative flex flex-col pt-1 pb-1 px-1 rounded-lg border transition-[border-color,background-color] duration-200 ease-out ${
          isCommentMode ? "min-h-[98px] max-h-[300px]" : ""
        } ${
          isDragOver
            ? "border-dashed border-blue-400/55 dark:border-blue-400/45 bg-blue-50/40 dark:bg-blue-950/20"
            : "border-border-gray dark:border-border-gray-dk"
        }`}
        {...(isCommentMode ? dragProps : {})}
        onClick={isCommentMode ? handleHeaderClick : undefined}
      >
        {isDragOver && isCommentMode && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-pure-white/60 dark:bg-pure-white-dk/60">
            <div className="flex flex-col items-center gap-1.5">
              <UploadIcon className="h-6 w-6 text-blue-400 dark:text-blue-500" />
              <p className="text-xs font-medium text-blue-500 dark:text-blue-400">
                Drop files here
              </p>
            </div>
          </div>
        )}
        <div
          className={`flex flex-col ${isCommentMode ? "flex-1 min-h-0" : "gap-4"} ${isDragOver && isCommentMode ? "pointer-events-none opacity-30" : ""}`}
        >
          {renderChannelSelector()}
          {isCommentMode ? (
            <>
              {renderCommentEditor()}
              {renderToolbar()}
            </>
          ) : (
            renderVerbaMailActions()
          )}
        </div>
      </div>
    </div>
  );
};
